import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  endpoint: string,
  options: RequestInit = {}
): Promise<any> {
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second base delay
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(endpoint, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        credentials: 'include',
      }).catch(networkError => {
        // Gracefully handle network errors
        console.warn('⚠️ Network error in apiRequest:', networkError.message);
        throw new Error(`Network error: ${networkError.message}`);
      });

      if (!res.ok) {
        // Gracefully parse error response - handle non-JSON
        let errorData: any;
        try {
          errorData = await res.json();
        } catch (parseError) {
          console.warn('⚠️ Failed to parse error response:', parseError);
          errorData = { message: res.statusText || 'Server error' };
        }

        // Don't retry on client errors (except 429)
        if (res.status >= 400 && res.status < 500 && res.status !== 429) {
          // For conflict errors (409), preserve conflict metadata
          if (res.status === 409) {
            const conflictError: any = new Error(errorData.message || 'Booking conflict');
            conflictError.conflictType = errorData.conflictType;
            conflictError.conflictingSession = errorData.conflictingSession;
            conflictError.todayCount = errorData.todayCount;
            throw conflictError;
          }
          
          throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
        }

        // Retry on server errors or rate limiting
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
          continue;
        }

        throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
      }

      // Gracefully parse successful response
      try {
        return await res.json();
      } catch (parseError) {
        console.warn('⚠️ Failed to parse JSON response:', parseError);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      // Network errors - retry with exponential backoff
      if (attempt < maxRetries && (error instanceof TypeError || error.message?.includes('Network error') || error.message?.includes('fetch'))) {
        console.log(`⏳ Retrying request (attempt ${attempt + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
        continue;
      }
      
      // Final failure - throw with clear message
      console.error('❌ API request failed after retries:', error);
      throw error;
    }
  }
  
  // Should never reach here, but handle gracefully
  throw new Error('Maximum retries exceeded');
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Database wake-up errors get more retries
        if (error?.message?.includes('waking up') || error?.message?.includes('endpoint has been disabled')) {
          return failureCount < 5;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 2s, 4s, 8s, 10s, 10s
        return Math.min(1000 * 2 ** attemptIndex, 10000);
      },
    },
    mutations: {
      retry: false,
    },
  },
});